/**
 * rateMotion — the style objects that put RATE_MOTION's dials onto elements.
 *
 * 🔴 ONE PLACE ASSEMBLES THE `animation` SHORTHAND. Components pass an index and
 * a stillness flag and get a style back; none of them writes a duration, an
 * easing or a keyframe name, so the dials file is genuinely the only place a
 * timing lives. rateMotion.test.js pins that by grepping the components for a
 * literal `123ms`, the same guard corpusMeter.test.js already runs on
 * CorpusMeter.
 *
 * 🔴 STILL MEANS FINISHED, NOT FROZEN. Every helper here answers
 * prefers-reduced-motion with the END state and `animation: none` — the card is
 * at full opacity and no transform, the bar is at full width, the number is at
 * its value. A reader who asked not to be moved sees the same page as everyone
 * else, arriving all at once.
 *
 * 🔴 THESE FIRE ONCE PER MOUNT BY CONSTRUCTION. A CSS animation restarts only
 * when its `animation` string changes; every string here is a pure function of
 * an index and a flag, both stable for the life of a card, so a re-render (a
 * card opening its picks, say) cannot replay the entrance. That is why this is
 * CSS animation rather than a state machine — there is no "have I run yet"
 * bookkeeping to get wrong.
 */

import { RATE_MOTION as M } from "../../data/rateMotion.js";
import "../../styles/rateMotion.css";

export { prefersReducedMotion } from "../LiveBoard/NominationFlash.jsx";

/** The end state, shared by every `still` branch below so they cannot diverge. */
const SETTLED = { animation: "none", opacity: 1, transform: "none" };

/**
 * How long after the first card the nth one starts.
 *
 * Capped, and that is the whole reason this is a function: without the cap a
 * 14-team room's last card would start at 780ms and land past a second, which
 * reads as a page still loading. Exported so the test can assert the cap holds
 * at the room sizes the product actually supports rather than trusting the
 * arithmetic in a comment.
 */
export function cardDelayMs(index) {
  return Math.min(index * M.cardStaggerMs, M.cardStaggerCapMs);
}

/** A team card lifting into place, best team first. */
export function cardEnter(index, still) {
  if (still) return SETTLED;
  return {
    "--rate-lift": `${M.cardLiftPx}px`,
    opacity: 0,
    animation: `rateCardIn ${M.cardRiseMs}ms ${M.easing} ${cardDelayMs(index)}ms forwards`,
  };
}

/**
 * The grade circle popping as its own card lands.
 *
 * Shares the card's delay rather than adding to it: the letter is what the eye
 * goes to, so it arrives WITH the card instead of a beat behind it.
 */
export function gradePop(index, still) {
  if (still) return SETTLED;
  return {
    opacity: 0,
    animation: `rateGradePop ${M.gradePopMs}ms ${M.easing} ${cardDelayMs(index)}ms forwards`,
  };
}

/** One position chip, in its own place in the row. */
export function chipEnter(index, still) {
  if (still) return SETTLED;
  return {
    opacity: 0,
    animation: `rateChipIn ${M.chipRiseMs}ms ${M.easing} ${index * M.chipStaggerMs}ms forwards`,
  };
}

/**
 * The spend bar growing out of its left edge.
 *
 * transformOrigin is part of the answer, not decoration: without it the bar
 * would grow from its middle and the segments would read as arriving rather
 * than as filling.
 */
export function barFill(still) {
  if (still) return { animation: "none", transform: "none", transformOrigin: "left center" };
  return {
    transformOrigin: "left center",
    animation: `rateBarFill ${M.barFillMs}ms ${M.easing} forwards`,
  };
}

/**
 * Anything a tap reveals.
 *
 * 🔴 OPEN ONLY, AND SAY SO. The expansions on these pages mount and unmount, so
 * there is no element left to animate on the way out — a close ease would mean
 * keeping every collapsed panel in the DOM, which puts its text in the
 * accessibility tree and in every test's textContent for the sake of a fifth
 * of a second nobody is looking at. The open is eased; the close is immediate.
 */
export function expandEnter(still) {
  if (still) return SETTLED;
  return {
    opacity: 0,
    animation: `rateExpandIn ${M.expandMs}ms ${M.easing} forwards`,
  };
}
